import React, { useState, useEffect, useRef } from 'react';
import { useForm, SubmitHandler, set } from 'react-hook-form';
import ReCaptcha from '../ReCaptcha/ReCaptcha';

interface FormField {
    id: string;
    name: string;
    type: string;
    label: string;
    required: boolean;
    value: string | boolean;
    attr: {
        placeholder?: string;
        rows?: number;
        [key: string]: any;
    };
    constraints: {
        required?: boolean;
        minLength?: number | null;
        email?: boolean;
        messages?: {
            required?: string;
            minLength?: string;
            email?: string;
        };
    };
}

interface FormConfig {
    fields: FormField[];
}

interface GatedContentFormProps {
    referer?: string;
    assetId: string;
    emailDocument: string;
    submitUrl: string;
    formConfig: FormConfig;
    recaptchaSiteKey: string;
    recaptchaVersion: 'checkbox' | 'score';
    isWebinar?: boolean;
    webinarEmbed?: string;
    webinarId?: string;
}

var GFormId = 0;

/**
 * Gated Content Form Component
 * Handles form validation and submission for gated content form
 */
export default function GatedContentForm({
    referer = '',
    assetId,
    emailDocument,
    submitUrl,
    formConfig,
    recaptchaSiteKey,
    recaptchaVersion,
    isWebinar,
    webinarEmbed,
    webinarId,
}: GatedContentFormProps) {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string>('');
    const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
    const [submitSuccessMessage, setSubmitSuccessMessage] = useState<string>('');
    const [activeFields, setActiveFields] = useState<Record<string, boolean>>({});
    const [recaptchaToken, setRecaptchaToken] = useState<string>('');
    const [recaptchaVersionUsed, setRecaptchaVersionUsed] = useState<string>('');
    const [formId, ] = useState(GFormId++);
    const [currentRecaptchaSiteKey, setCurrentRecaptchaSiteKey] = useState<string>(recaptchaSiteKey);
    const [currentRecaptchaVersion, setCurrentRecaptchaVersion] = useState<'checkbox' | 'score'>(recaptchaVersion);
    const [showWebinar, setShowWebinar] = useState<boolean>(false);
    const formRef = useRef<HTMLFormElement>(null);

    const getId = (id: string) => `${id}-${formId}`;

    /**
     * Handles showing webinar embed and hiding form container
     * Uses ref to find parent containers relative to this specific form instance
     */
    useEffect(() => {
        if (!isWebinar || !showWebinar || !webinarEmbed || !formRef.current) {
            return;
        }

        const gatedContent = formRef.current.closest('.gated-content');
        const formContainer = gatedContent?.querySelector('.gated-content__form-container');
        const embedRow = gatedContent?.querySelector('.gated-content__embed');
        const embedContainer = embedRow?.querySelector('.col-12');

        if (formContainer) {
            (formContainer as HTMLElement).classList.add('d-none');
        }

        if (embedContainer) {
            embedContainer.innerHTML = webinarEmbed;

            if (embedRow) {
                (embedRow as HTMLElement).classList.remove('d-none');
            }
        }
    }, [isWebinar, showWebinar, webinarEmbed]);

    /**
     * Handles token change from ReCaptcha component
     * Captures both token and the version used to generate it
     */
    const handleRecaptchaTokenChange = (token: string) => {
        setRecaptchaToken(token);
        setRecaptchaVersionUsed(currentRecaptchaVersion);
    };

    /**
     * Build default values dynamically from formConfig and props
     */
    const buildDefaultValues = () => {
        const propsMap: Record<string, any> = { referer, assetId, emailDocument, isWebinar, webinarId };
        const defaults: Record<string, any> = {};

        formConfig.fields.forEach(field => {
            const fieldKey = field.name.match(/\[([^\]]+)\]$/)?.[1];

            if (field.type === 'checkbox') {
                defaults[field.name] = false;
            } else if (fieldKey && propsMap[fieldKey] !== undefined) {
                defaults[field.name] = propsMap[fieldKey];
            } else if (field.value !== undefined && field.value !== '') {
                defaults[field.name] = field.value;
            }
        });

        return defaults;
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm({
        defaultValues: buildDefaultValues()
    });

    /**
     * Get field error from nested error object
     */
    const getFieldError = (fieldName: string) => {
        // For names like "gated_content_form[firstName]", extract the nested path
        const match = fieldName.match(/^([^\[]+)\[([^\]]+)\]$/);

        if (match) {
            const [, formName, fieldKey] = match;
            const formErrors = errors[formName] as Record<string, any> | undefined;
            return formErrors?.[fieldKey];
        }

        return errors[fieldName];
    };

    /**
     * Builds validation rules from field constraints
     */
    const buildValidationRules = (field: FormField) => {
        const rules: any = {};
        const messages = field.constraints.messages || {};

        if (field.required) {
            rules.required = messages.required || field.label + ' is required';
        }

        if (field.constraints.minLength) {
            rules.minLength = {
                value: field.constraints.minLength,
                message: messages.minLength || `${field.label} must be at least ${field.constraints.minLength} characters`
            };
        }

        if (field.constraints.email) {
            rules.pattern = {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: messages.email || 'Invalid email address'
            };
        }

        return rules;
    };

    /**
     * Handles setting active field on focus
     */
    const handleFocus = (name: string) => {
        setActiveFields((prev) => ({ ...prev, [name]: true }));
    };

    /**
     * Handles setting active field on blur
     */
    const handleBlur = (name: string, value: string) => {
        setActiveFields((prev) => ({ ...prev, [name]: value !== '' }));
    };

    /**
     * Toggles active class
     */
    const toggleClass = (
        el: HTMLElement | null,
        className: string,
        shouldAdd: boolean
    ) => {
        if (!el) return;
    
        if (shouldAdd) {
            el.classList.add(className);
        } else {
            el.classList.remove(className);
        }
    };

    /**
     * Handles form submission
     */
    const onSubmit: SubmitHandler<any> = async (_data, event) => {
        setIsSubmitting(true);
        setSubmitError('');

        try {
            const formElement = event?.target as HTMLFormElement;
            const formData = new FormData(formElement);

            if (recaptchaToken) {
                formData.append('g-recaptcha-response', recaptchaToken);
                formData.append('recaptcha-version-hidden', recaptchaVersionUsed);
            }

            const response = await fetch(submitUrl, {
                method: 'POST',
                body: formData
            });

            const result = await response.json();

            if (!response.ok || result.status != 'success') {
                if (result.reCAPTCHA_version && result.reCAPTCHA_site_key) {
                    setCurrentRecaptchaVersion(result.reCAPTCHA_version);
                    setCurrentRecaptchaSiteKey(result.reCAPTCHA_site_key);
                    setRecaptchaToken('');
                    setRecaptchaVersionUsed('');
                    setSubmitError(result.message || 'Please complete the reCAPTCHA verification');
                    setIsSubmitting(false);
                    return;
                }

                throw new Error(result.message || 'Form submission failed');
            }

            if (result.data?.webinarAccessGranted) {
                setShowWebinar(result.data?.webinarAccessGranted);
            }

            setSubmitSuccess(true);
            setSubmitSuccessMessage(result.message);
            reset();
            setActiveFields({});
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : 'An error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    /**
     * Renders a form field based on its type
     */
    const renderField = (field: FormField) => {
        if (field.type === 'submit') return null;

        const fieldError = getFieldError(field.name);
        const isActive = !!activeFields[getId(field.id)];

        if (field.type === 'hidden') {
            return (
                <div key={getId(field.id)}>
                    <input
                        id={getId(field.id)}
                        type="hidden"
                        {...register(field.name, buildValidationRules(field))}
                    />
                </div>
            )
        }

        if (field.type === 'checkbox') {
            return (
                <div
                    key={getId(field.id)}
                    className="form__group form__group--checkbox"
                    data-aos="fade-up"
                >
                    <label htmlFor={getId(field.id)} className="checkbox">
                        <input
                            id={getId(field.id)}
                            type="checkbox"
                            className="form__checkbox"
                            {...register(field.name, buildValidationRules(field))}
                        />
                        <div className="checkbox__icon">
                            <svg fill="none" width="10" height="8" viewBox="0 0 10 8" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M4.94041 6.47654L9.89011 1.52683L8.4759 0.112618L3.52619 5.06232L1.41357 2.94971L-0.000639211 4.36392L2.11198 6.47654L2.11194 6.47658L3.52615 7.89079L3.52619 7.89075L3.52623 7.89079L4.94044 6.47657L4.94041 6.47654Z" fill="#FFFFFF"></path>
                            </svg>
                        </div>
                        <div>{field.label}</div>
                    </label>
                    {fieldError && (
                        <div className="form__error d-block">
                            {fieldError.message as string}
                        </div>
                    )}
                </div>
            );
        }

        const inputProps = {
            id: getId(field.id),
            className: `form__input ${fieldError ? 'form__error' : ''}`,
            ...register(field.name, buildValidationRules(field)),
            onFocus: () => handleFocus(getId(field.id)),
            onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => handleBlur(getId(field.id), e.target.value)
        };

        return (
            <div key={getId(field.id)} className="form__group" ref={(el) => toggleClass(el, 'form__group--active', isActive)} data-aos="fade-up">
                <label htmlFor={getId(field.id)} className={`form__label ${isActive ? 'form__label--active' : ''}`}>
                    {field.label}
                </label>
                {field.type === 'textarea' ? (
                    <textarea
                        {...inputProps}
                        rows={field.attr.rows || 1}
                    />
                ) : (
                    <input
                        {...inputProps}
                        type={field.type}
                    />
                )}
                {fieldError && (
                    <div className="form__error">{fieldError.message as string}</div>
                )}
            </div>
        );
    };

    const submitField = formConfig.fields.find(field => field.type === 'submit');

    return (
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)} className="form form--gated-content" method="post" noValidate>
            {formConfig.fields.map(field => renderField(field))}

            <ReCaptcha
                key={`${currentRecaptchaVersion}-${currentRecaptchaSiteKey}`}
                siteKey={currentRecaptchaSiteKey}
                version={currentRecaptchaVersion}
                onTokenChange={handleRecaptchaTokenChange}
                className={`form-recaptcha ${currentRecaptchaVersion == 'checkbox' ? 'form__group' : ''}`}
            />

            {!submitSuccess && (
                <div className="form__group form__group--submit" data-aos="fade-up">
                    <button
                        type="submit"
                        className="btn btn--secondary"
                        disabled={isSubmitting}
                    >
                        {submitField?.label}
                    </button>
                    {submitError && (
                        <div className="form__error">
                            {submitError}
                        </div>
                    )}
                </div>
            )}

            {submitSuccess && submitSuccessMessage && (
                <div className="form__group">
                    <div className="text-bold">
                        {submitSuccessMessage}
                    </div>
                </div>
            )}
        </form>
    );
}
